import { createPortal } from "react-dom";
import { CloseOutlined } from "@ant-design/icons";
import { Resizable } from "re-resizable";
import useFloatingPanel from "hooks/useFloatingPanel";
import "./FloatingPanel.scss";

const RESIZE_ENABLE = {
  top: true,
  right: true,
  bottom: true,
  left: true,
  topRight: true,
  bottomRight: true,
  bottomLeft: true,
  topLeft: true,
};

const RESIZE_HANDLE_STYLES = {
  top: {
    height: "10px",
    top: 0,
    left: 0,
    width: "100%",
    cursor: "ns-resize",
  },
  right: {
    width: "10px",
    right: 0,
    top: 0,
    height: "100%",
    cursor: "ew-resize",
  },
  bottom: {
    height: "10px",
    bottom: 0,
    left: 0,
    width: "100%",
    cursor: "ns-resize",
  },
  left: {
    width: "10px",
    left: 0,
    top: 0,
    height: "100%",
    cursor: "ew-resize",
  },
  topRight: {
    width: "16px",
    height: "16px",
    right: 0,
    top: 0,
    cursor: "nesw-resize",
  },
  bottomRight: {
    width: "20px",
    height: "20px",
    right: 0,
    bottom: 0,
    cursor: "nwse-resize",
  },
  bottomLeft: {
    width: "16px",
    height: "16px",
    left: 0,
    bottom: 0,
    cursor: "nesw-resize",
  },
  topLeft: {
    width: "16px",
    height: "16px",
    left: 0,
    top: 0,
    cursor: "nwse-resize",
  },
};

const RESIZE_HANDLE_CLASSES = {
  top: "floating-panel__resize-edge floating-panel__resize-edge--top",
  right: "floating-panel__resize-edge floating-panel__resize-edge--right",
  bottom: "floating-panel__resize-edge floating-panel__resize-edge--bottom",
  left: "floating-panel__resize-edge floating-panel__resize-edge--left",
  topRight: "floating-panel__resize-grip floating-panel__resize-grip--top-right",
  bottomRight: "floating-panel__resize-grip floating-panel__resize-grip--bottom-right",
  bottomLeft: "floating-panel__resize-grip floating-panel__resize-grip--bottom-left",
  topLeft: "floating-panel__resize-grip floating-panel__resize-grip--top-left",
};

export default function FloatingPanel({
  open,
  onClose,
  title,
  dark = false,
  children,
  className = "",
  bodyClassName = "",
  panelOptions,
  // Overrides the host's stacking (default 1100 in the SCSS), e.g. to step
  // below an antd Modal (1000) the panel opened.
  zIndex,
}) {
  const {
    bounds,
    panelRef,
    minWidth,
    minHeight,
    onHeaderMouseDown,
    onResizeStart,
    onResize,
  } = useFloatingPanel(open, panelOptions);

  if (!open) return null;

  const container = document.getElementById("content-root") || document.body;

  return createPortal(
    <div
      ref={panelRef}
      className="floating-panel-host"
      style={{
        left: bounds.x,
        top: bounds.y,
        ...(zIndex != null ? { zIndex } : {}),
      }}
      role="dialog"
      aria-modal="false"
      aria-label={typeof title === "string" ? title : undefined}
    >
      <Resizable
        size={{ width: bounds.width, height: bounds.height }}
        minWidth={minWidth}
        minHeight={minHeight}
        bounds="window"
        enable={RESIZE_ENABLE}
        handleStyles={RESIZE_HANDLE_STYLES}
        handleClasses={RESIZE_HANDLE_CLASSES}
        onResizeStart={onResizeStart}
        onResize={onResize}
        className={`floating-panel${dark ? " floating-panel--dark" : ""}${
          className ? ` ${className}` : ""
        }`}
      >
        <div className="floating-panel__header" onMouseDown={onHeaderMouseDown}>
          <span className="floating-panel__title">{title}</span>
          <button
            type="button"
            className="floating-panel__close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
        </div>

        <div
          className={`floating-panel__body${
            bodyClassName ? ` ${bodyClassName}` : ""
          }`}
        >
          {children}
        </div>
      </Resizable>
    </div>,
    container
  );
}
